import { Dispatch, SetStateAction, useCallback } from "react";
import { useTransactionStore } from "../../stores/useTransactionStore";
import {
    TransactionDetailResponseModel,
    TransactionResponseModel,
    ValidationErrorModel,
    ToastModel,
    transactionPrintRequestModel,
    SearchTransactionRequestModel,
    ComponentActionTypeEnum,
    TransactionItemResponseModel,
    UploadedFileModel, DailyTransactionTypeEnum,
} from "../../../../../models";
import {generateGuid} from "../../../../../utils";

type SetStateType<T> = T | ((prev: T) => T);

/**
 * Resolves Zustand-compatible state update values
 */
const resolveSetValue = <T>(value: SetStateType<T>, current: T): T => {
    return typeof value === "function" ? (value as (prev: T) => T)(current) : value;
};

export const useTransactionActions = () => {
    const {
        setObject,
        setRefundObject,
        setResponseObject,
        setTransactionItemObject,
        setPrintObject,
        setList,
        setValidationErrors,
        setUploadedFiles,
        setToastModel,
        setLoading,
        setShowDeleteModal,
        setShowPaymentDetailsModel,
        setShowRefundItemModel,
        setComponentActionType,
        setTotalRows,
        setResetKey,
        setSearchRequest,
        setRefreshListTrigger,
        setTransactionActivePageKey,
        setTransactionType,
        setGuidKey,
        resetState,
    } = useTransactionStore();

    const handleSetObject = useCallback((object: TransactionDetailResponseModel | null) => {
        const resolved = resolveSetValue(object, useTransactionStore.getState().object);
        const startTime = performance.now();
        setObject(resolved);
        const duration = performance.now() - startTime;
        console.log(`Setting transaction object took ${duration.toFixed(2)}ms`);
    }, [setObject]);

    const handleSetRefundObject = useCallback((object: TransactionDetailResponseModel | null) => {
        const resolved = resolveSetValue(object, useTransactionStore.getState().refundObject);
        const startTime = performance.now();
        setRefundObject(resolved);
        const duration = performance.now() - startTime;
        console.log(`Setting refund object took ${duration.toFixed(2)}ms`);
    }, [setRefundObject]);

    const handleSetResponseObject = useCallback((object: TransactionResponseModel | null) => {
        const resolved = resolveSetValue(object, useTransactionStore.getState().responseObject);
        const startTime = performance.now();
        setResponseObject(resolved);
        const duration = performance.now() - startTime;
        console.log(`Setting response object took ${duration.toFixed(2)}ms`);
    }, [setResponseObject]);

    const handleSetTransactionItemObject = useCallback((object: TransactionItemResponseModel | null) => {
        //console.trace('res_x_1_STACK TRACE:'); // This will show the exact calling function!
        const resolved = resolveSetValue(object, useTransactionStore.getState().transactionItemObject);
        const startTime = performance.now();
        setTransactionItemObject(resolved);
        const duration = performance.now() - startTime;
        console.log(`Setting item object took ${duration.toFixed(2)}ms`);
    }, [setTransactionItemObject]);

    const handleSetPrintObject = useCallback((object: transactionPrintRequestModel) => {
        const resolved = resolveSetValue(object, useTransactionStore.getState().printObject);
        setPrintObject(resolved);
    }, [setPrintObject]);

    const handleSetList = useCallback((list: TransactionResponseModel[]) => {
        const resolved = resolveSetValue(list, useTransactionStore.getState().list);
        setList(resolved);
    }, [setList]);

    const handleSetValidationErrors = useCallback((errors?: ValidationErrorModel[] | null) => {
        const resolved = resolveSetValue(errors ?? [], useTransactionStore.getState().validationErrors);
        setValidationErrors(resolved);
    }, [setValidationErrors]);

    const handleSetUploadedFiles = useCallback((files?: UploadedFileModel[] | null) => {
        const resolved = resolveSetValue(files ?? [], useTransactionStore.getState().uploadedFiles);
        setUploadedFiles(resolved);
    }, [setUploadedFiles]);

    const handleSetToastModel = useCallback((toast: ToastModel) => {
        const resolved = resolveSetValue(toast, useTransactionStore.getState().toastModel);
        setToastModel(resolved);
    }, [setToastModel]);

    const handleSetLoading: Dispatch<SetStateAction<boolean>> = useCallback((value) => {
        const resolved = resolveSetValue(value, useTransactionStore.getState().loading);
        //trackLoadingStateChange(resolved);
        setLoading(resolved);
    }, [setLoading]);

    const handleSetShowDeleteModal: Dispatch<SetStateAction<boolean>> = useCallback((value) => {
        const resolved = resolveSetValue(value, useTransactionStore.getState().showDeleteModal);
        setShowDeleteModal(resolved);
    }, [setShowDeleteModal]);

    const handleSetShowRefundItemModel: Dispatch<SetStateAction<boolean>> = useCallback((value) => {
        const resolved = resolveSetValue(value, useTransactionStore.getState().showRefundItemModel);
        setShowRefundItemModel(resolved);
    }, [setShowRefundItemModel]);

    const handleSetShowPaymentDetailsModel: Dispatch<SetStateAction<boolean>> = useCallback((value) => {
        const resolved = resolveSetValue(value, useTransactionStore.getState().showPaymentDetailsModel);
        setShowPaymentDetailsModel(resolved);
    }, [setShowPaymentDetailsModel]);

    const handleSetComponentActionType = useCallback((type: ComponentActionTypeEnum) => {
        const resolved = resolveSetValue(type, useTransactionStore.getState().componentActionType);
        setComponentActionType(resolved);
    }, [setComponentActionType]);

    const handleSetTotalRows :Dispatch<SetStateAction<number>> =  useCallback((total) => {
        const resolved = resolveSetValue(total, useTransactionStore.getState().totalRows);
        setTotalRows(resolved);
    }, [setTotalRows]);

    const handleSetResetKey: Dispatch<SetStateAction<number>> = useCallback((value) => {
        const resolved = resolveSetValue(value, useTransactionStore.getState().resetKey);
        setResetKey(resolved);
    }, [setResetKey]);

    const handleSetSearchRequest = useCallback((searchRequest: SearchTransactionRequestModel) => {
        const resolved = resolveSetValue(searchRequest, useTransactionStore.getState().searchRequest);
        setSearchRequest(resolved);
    }, [setSearchRequest]);

    const handleSetTransactionType = useCallback((transactionType: DailyTransactionTypeEnum) => {
        const resolved = resolveSetValue(transactionType, useTransactionStore.getState().transactionType);
        setTransactionType(resolved);
    }, [setTransactionType]);

    const handleSetGuidKey: Dispatch<SetStateAction<string>> = useCallback((value) => {
        const resolved = resolveSetValue(value, useTransactionStore.getState().guidKey);
        setGuidKey(resolved);
    }, [setGuidKey]);

    const handleResetState = useCallback(() => {
        resetState();
    }, [resetState]);

    const handleRefreshListTrigger: Dispatch<SetStateAction<string>> = useCallback((value) => {
        const newValue = value == null ? generateGuid() : value;
        const resolved = resolveSetValue(newValue, useTransactionStore.getState().refreshListTrigger);
        setRefreshListTrigger(resolved);
    }, [setRefreshListTrigger]);

    const handleTransactionActivePageKey: Dispatch<SetStateAction<string>> = useCallback((value) => {
        const newValue = value == null ? generateGuid() : value;
        const resolved = resolveSetValue(newValue, useTransactionStore.getState().transactionActivePageKey);
        setTransactionActivePageKey(resolved);
    }, [setTransactionActivePageKey]);

    return {
        handleSetObject,
        handleSetResponseObject,
        handleSetTransactionItemObject,
        handleSetPrintObject,
        handleSetList,
        handleSetValidationErrors,
        handleSetUploadedFiles,
        handleSetToastModel,
        handleSetLoading,
        handleSetShowDeleteModal,
        handleSetShowRefundItemModel,
        handleSetShowPaymentDetailsModel,
        handleSetRefundObject,
        handleSetSearchRequest,
        handleSetComponentActionType,
        handleSetTotalRows,
        handleSetResetKey,
        handleSetGuidKey,
        handleSetTransactionType,
        handleResetState,
        handleRefreshListTrigger,
        handleTransactionActivePageKey
    };
};

// Optional helper for analytics or debugging
const trackLoadingStateChange = (loading: boolean) => {
    if (loading) {
        console.info("Analytics: Loading started");
    } else {
        console.info("Analytics: Loading completed");
    }
};
